import type { ReactNode } from 'react';
import { useMemo } from 'react';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Banners } from '@sorare/core/src/components/layout/Banners';
import { MobileAppBottomPortalReceiver } from '@sorare/core/src/components/navigation/AppNavigation';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { useTitleAndDescription } from '@sorare/core/src/hooks/useTitleAndDescription';
import { createCardSorts } from '@sorare/core/src/lib/algolia';
import type { FiltersGroup } from '@sorare/core/src/lib/filters';
import { metadatas } from '@sorare/core/src/lib/seo/football';

import { useDefaultFilters } from 'hooks/useDefaultFilters';
import { AdvancedCardSearch } from 'searchCards';

import { NewsSignings } from '../OnboardingDialog/NewsSignings';
import { PageTemplate } from '../PageTemplate';

type Props = {
  primaryMarketFilters: FiltersGroup[];
  banner: ReactNode;
};

export const NewSignings = ({ primaryMarketFilters, banner }: Props) => {
  const { isIosApp, isMarketAuthorizedIosApp } = useIsMobileApp();

  useTitleAndDescription(
    metadatas.newSignings.title,
    metadatas.newSignings.description
  );
  const filters = useDefaultFilters({
    auction: true,
  });

  const cardSorts = useMemo(
    () =>
      createCardSorts({
        indexPrefix: 'Cards On Sale',
        withHighestAverageScore: true,
      }),
    []
  );

  const defaultSort = 'Cards On Sale Ending Soon';

  return (
    <>
      {!!(!isIosApp || isMarketAuthorizedIosApp) && (
        <>
          <NewsSignings />
          <Banners />
        </>
      )}
      <PageTemplate showBack>
        <AdvancedCardSearch
          sport={Sport.FOOTBALL}
          cardFilters={primaryMarketFilters}
          sorts={cardSorts}
          defaultSort={defaultSort}
          defaultFilters={filters}
          analyticsTags={['NewSignings', 'Football']}
          onlyPrimary
          removeFinishedAuctions
          hideSorareUser
          banner={banner}
          distinct
          displayVideoTutorial
        />
      </PageTemplate>
      {!!isIosApp && (
        <MobileAppBottomPortalReceiver id="above-bottom-bar-portal" />
      )}
    </>
  );
};
